import React, { useEffect, useRef, useState } from "react";
import axios, { CancelTokenSource, CanceledError } from "axios";
import { useFieldArray, useForm } from "react-hook-form";
import { useLocation } from "react-router";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { LoadingContainer } from "go-core/components/Loading";
import { useWindowSize } from "go-core/components/useWindowSize";
import { FormDirty } from "go-form/components/FormDirty";
import { organizationApi } from "../../../../../../../../services/Api/Organization/organizationApi";
import {
	AddonApi,
	AvailableSubscriptionContentsApi,
	BaseSubscriptionApi,
	SubscriptionApi,
	SubscriptionInvoiceApi,
	SubscriptionVisibility,
	SubscriptionsApi,
	UpdateAddonApi,
	UpdateBaseSubscriptionApi,
	UpdateSubscriptionApi,
	UpdateSubscriptionsApi,
} from "../../../../../../../../services/Api/Subscription/subscriptionTypes";
import {
	OrganizationMeApi,
	PriceConfiguratorContentApi,
	PriceConfiguratorPlanApi,
} from "../../../../../../../../services/Api/types";
import SubscriptionSummaryPreview from "../SummaryPreview/SubscriptionSummaryPreview";
import InactiveSubscription from "./InactiveSubscription";
import ReadonlySubscription from "./ReadonlySubscription";
import StickySubscriptionsSummary from "./StickySubscriptionsSummary";
import SubscriptionsPaymentSummaryCard from "./SubscriptionsPaymentSummaryCard";
import SubscriptionConfigForm from "./components/SubscriptionConfigForm";
import {
	defaultAddons,
	findSameSubscription,
	getLicenseLevel,
	isArchivePlan,
	isSubscriptionActive,
	parseSubscriptionsToRequest,
} from "./services";

interface Props {
	subscriptions: SubscriptionsApi;
	config: AvailableSubscriptionContentsApi;
	meDetails: OrganizationMeApi;
	showSummaryPreview: boolean;
	setShowSummaryPreview: (value: boolean) => void;
	subscriptionsVisibility: SubscriptionVisibility;
	licenseLevels: any[];
	invoices: SubscriptionInvoiceApi[];
}

const parseSubscriptionToAddonUpdate = (addon: AddonApi) => {
	return {
		addon_id: addon.addon_id,
		quantity: addon.quantity,
	} as UpdateAddonApi;
};
const parseSubscriptionToUpdate = (subscription?: BaseSubscriptionApi) => {
	if (!subscription) return undefined;
	return {
		external_id: subscription.edit_mode === "READ_ONLY" ? subscription.external_id : undefined,
		edit_mode: subscription.edit_mode,
		family_id: subscription.family_id,
		billing_period: subscription.billing_period,
		plan_id: subscription.plan_id,
		addons: subscription.addons.map((addon) => {
			return parseSubscriptionToAddonUpdate(addon);
		}),
		status: !subscription.status ? "ACTIVE" : subscription.status,
		cancellation_reason: subscription.cancellation_reason,
		reason_details: subscription.reason_details,
	} as UpdateSubscriptionApi;
};
const parseSubscriptionsToUpdate = (subscriptions: SubscriptionsApi) => {
	return {
		subscriptions: subscriptions.subscriptions.map((subscription) => {
			return {
				...parseSubscriptionToUpdate(subscription),
				monthly_sub_item: parseSubscriptionToUpdate(subscription.monthly_sub_item),
			};
		}),
		license_levels: subscriptions.license_levels,
	} as UpdateSubscriptionsApi;
};

const getParsedSubscriptionBasedOnUrlParams = (
	subscription: UpdateSubscriptionApi,
	config: AvailableSubscriptionContentsApi,
	plan: string | null,
	addonId: string | null,
	addonValue: string | null,
	addonPlusValue: string | null,
	subscriptionConfig?: PriceConfiguratorContentApi
) => {
	const configForSubscription =
		subscriptionConfig ?? config.contents.find(({ family_id }) => subscription.family_id === family_id);
	if (!configForSubscription) return subscription;

	if (plan) {
		const configPlan = configForSubscription.plans.find(({ plan_id }) => plan_id === plan);
		if (configPlan) subscription.plan_id = configPlan.plan_id;
	}

	if (addonId) {
		const planAddons = configForSubscription.plans.find((subscriptionPlan) =>
			subscriptionPlan.addons.some((addon) => addon.addon_id === addonId)
		)?.addons;
		if (planAddons) {
			const addon = planAddons.find(({ addon_id }) => addon_id === addonId);

			if (addon) {
				const subscriptionAddon = subscription.addons.find(({ addon_id }) => addon_id === addon.addon_id);
				if (subscriptionAddon) {
					if (addon.details.value_type === "BOOLEAN") {
						if (addonValue !== null) {
							subscriptionAddon.quantity = Number(addonValue) ? 1 : 0;
						}
					} else if (addonValue !== null && !isNaN(Number(addonValue))) {
						subscriptionAddon.quantity = Number(addonValue);
					} else if (addonPlusValue !== null && !isNaN(Number(addonPlusValue))) {
						if (!subscriptionAddon.quantity) {
							subscriptionAddon.quantity = Number(addonPlusValue);
						} else {
							subscriptionAddon.quantity += Number(addonPlusValue);
						}
					}

					const subscriptionAddonIndex = subscription.addons.findIndex(
						({ addon_id }) => addon_id === subscriptionAddon.addon_id
					);
					if (subscriptionAddonIndex > -1) {
						subscription.addons[subscriptionAddonIndex] = subscriptionAddon;
					}
				} else {
					const newAddon: UpdateAddonApi = { addon_id: addon.addon_id, quantity: null };
					if (addon.details.value_type === "BOOLEAN") {
						if (addonValue !== null) {
							newAddon.quantity = Number(addonValue) ? 1 : 0;
						} else {
							newAddon.quantity = 1;
						}
					} else if (addonValue !== null && !isNaN(Number(addonValue))) {
						newAddon.quantity = Number(addonValue);
					} else {
						newAddon.quantity = 1;
					}

					subscription.addons.push(newAddon);
				}
			}
		}
	}

	return subscription;
};

const parseSubscriptionsBasedOnUrlParams = (
	subscriptions: UpdateSubscriptionApi[],
	search: string,
	config: AvailableSubscriptionContentsApi
) => {
	const urlParams = new URLSearchParams(search);
	const plan = urlParams.get("plan");
	const addonId = urlParams.get("addon_id");
	const addonValue = urlParams.get("addon_value");
	const addonPlusValue = urlParams.get("addon_plus_value");

	if (!plan && !addonId) {
		return subscriptions;
	}

	const existingSubscriptionWithFamilyId = subscriptions.find((subscription) => {
		if (plan) {
			return plan.toLowerCase().includes(subscription.family_id.toLowerCase()) ? subscription : undefined;
		}
		if (addonId) {
			return addonId.toLowerCase().includes(subscription.family_id.toLowerCase()) ? subscription : undefined;
		}

		return undefined;
	});

	if (existingSubscriptionWithFamilyId) {
		return subscriptions.map((subscription) => {
			return getParsedSubscriptionBasedOnUrlParams(
				subscription,
				config,
				plan,
				addonId,
				addonValue,
				addonPlusValue
			);
		});
	}

	if (!plan) {
		return subscriptions;
	}

	const configForSubscription = config.contents.find(({ family_id }) =>
		plan.toLowerCase().includes(family_id.toLowerCase())
	);
	if (!configForSubscription) return subscriptions;

	const subscriptionPlan = configForSubscription.plans.find(({ plan_id }) => plan_id === plan);
	if (!subscriptionPlan) return subscriptions;

	const newSubscriptions = [...subscriptions];
	const subscriptionToParse = {
		plan_id: plan,
		family_id: configForSubscription.family_id,
		addons: [],
		edit_mode: subscriptionPlan.edit_mode,
		billing_period: subscriptionPlan.billing_periods[0],
		name: subscriptionPlan.name,
		status: "ACTIVE",
		external_id: "",
	} as UpdateSubscriptionApi;
	const newSubscription = getParsedSubscriptionBasedOnUrlParams(
		subscriptionToParse,
		config,
		plan,
		addonId,
		addonValue,
		addonPlusValue,
		configForSubscription
	);
	newSubscriptions.push(newSubscription);

	return newSubscriptions;
};

const parseToEstimate = (estimate: SubscriptionsApi, values: UpdateSubscriptionsApi) => {
	values.subscriptions.forEach((value) => {
		const subscription = findSameSubscription(
			estimate.subscriptions,
			value.family_id,
			value.edit_mode,
			value.external_id
		);
		if (subscription) {
			subscription.status = value.status;
			subscription.external_id = value.external_id;
		}
	});
	return estimate;
};

const SubscriptionsFormNew = (props: Props) => {
	const subscriptions = props.subscriptions;
	const configs = props.config;
	const setShowSummaryPreview = props.setShowSummaryPreview;
	const subscriptionsVisibility = props.subscriptionsVisibility;
	const { search } = useLocation();
	const form = useForm<UpdateSubscriptionsApi>({
		criteriaMode: "all",
		shouldUnregister: false,
		defaultValues: parseSubscriptionsToUpdate(subscriptions),
	});
	const [estimateSubscriptions, setEstimateSubscriptions] = useState<SubscriptionsApi>(subscriptions);
	const [estimating, setEstimating] = useState(false);
	const { addFlash } = useFlash();
	const [loading, setLoading] = useState(true);
	const debounceTimeoutRef = useRef<any>(null);
	const doNotEstimateAfterEstimateUpdate = useRef(false);
	const firstRender = useRef(true);
	const [estimateCancelTokenSource, setEstimateCancelTokenSource] = useState<CancelTokenSource | undefined>();
	const {
		setError,
		control,
		formState,
		setValue,
		getValues,
		clearErrors,
		watch,
		formState: { errors },
	} = form;
	const isMobile = useWindowSize().isMobile;
	const { append, remove } = useFieldArray({
		control,
		name: "subscriptions",
	});
	const watchSubscriptions = watch(`subscriptions`);
	const watchSubscriptionsJson = JSON.stringify(watchSubscriptions);
	const [termsNamesToAccept, setTermsNamesToAccept] = useState<string[]>([]);

	useEffect(() => {
		if (search) {
			setValue("subscriptions", parseSubscriptionsBasedOnUrlParams(watchSubscriptions, search, configs));
			estimate();
		}
	}, [search]);

	const debounceEstimate = () => {
		clearTimeout(debounceTimeoutRef.current);
		debounceTimeoutRef.current = setTimeout(() => {
			if (firstRender.current) {
				firstRender.current = false;
				setLoading(false);
			} else if (doNotEstimateAfterEstimateUpdate.current) {
				doNotEstimateAfterEstimateUpdate.current = false;
			} else {
				estimate();
			}
		}, 1);
	};

	useEffect(() => {
		debounceEstimate();
	}, [watchSubscriptionsJson]);

	const onAddSubscription = (config: PriceConfiguratorContentApi) => {
		if (watchSubscriptions.some((f) => f.edit_mode === "EDITABLE" && f.family_id === config.family_id)) {
			return;
		}
		append(createDefaultSubscription(config));
	};
	const handleRemoveSubscription = (index: number) => {
		remove(index);
		handleRemoveTermsToAcceptForSubscription(index);
	};

	const handleRemoveTermsToAcceptForSubscription = (index: number) => {
		const subscription = watchSubscriptions[index];
		const newTermsNames = [...termsNamesToAccept];

		subscription.addons.forEach((addon) => {
			if (addon.terms?.name && newTermsNames.includes(addon.terms.name)) {
				const termsNameIndex = newTermsNames.indexOf(addon.terms.name);
				newTermsNames.splice(termsNameIndex, 1);
			}
		});
		setTermsNamesToAccept(newTermsNames);
	};

	const estimate = async (formValues?: SubscriptionsApi) => {
		if (estimateCancelTokenSource !== undefined) {
			estimateCancelTokenSource.cancel();
		}
		const CancelToken = axios.CancelToken;
		const newCancelToken = CancelToken.source();
		setEstimateCancelTokenSource(newCancelToken);
		try {
			setEstimating(true);
			const values = formValues ? formValues : getValues();
			if (!values) return;
			const newValues = {
				...values,
				subscriptions: parseSubscriptionsToRequest(configs, values).filter((subscription) =>
					isSubscriptionActive(subscription.status)
				),
			};
			const res = await organizationApi.estimateSubscription(newValues, { cancelToken: newCancelToken.token });
			const resSubscription = res.data.data;
			setEstimateSubscriptions(parseToEstimate(resSubscription, values));
			setEstimating(false);
			clearErrors();
		} catch (e) {
			if (e instanceof CanceledError) {
				return;
			}
			setEstimating(false);
			handleError.form(e, setError, addFlash);
		}
	};

	const handleUpdateSubscription = (index: number, value: UpdateSubscriptionApi) => {
		setValue(`subscriptions.${index}`, value);
	};

	const handleChangePlan = async (familyId: string, planId: string) => {
		watchSubscriptions
			.filter((x) => x.family_id === familyId)
			.forEach((field) => {
				const index = watchSubscriptions.indexOf(field);
				const newPlan = getPlanBaseOtherPlan(familyId, planId, field.plan_id, true);
				if (!newPlan) return;

				const configFamily = props.config.contents.find((x) => x.family_id === familyId);
				setValue(`subscriptions.${index}.plan_id`, newPlan.plan_id);
				if (configFamily) {
					const newAddons = defaultAddons(configFamily, newPlan.plan_id, field.addons);
					setValue(`subscriptions.${index}.addons`, newAddons);
				}
			});
	};

	const createDefaultSubscription = (config: PriceConfiguratorContentApi, original?: SubscriptionApi) => {
		let originalSubscription =
			original && !isArchivePlan(config, original.plan_id) ? parseSubscriptionToUpdate(original) : undefined;
		if (!originalSubscription) {
			originalSubscription = {
				family_id: config.family_id,
				status: "ACTIVE",
				addons: defaultAddons(config, undefined, undefined),
				plan_id: config.plans[0].plan_id,
				billing_period: config.plans[0]?.billing_periods[0],
				edit_mode: config.plans[0]?.edit_mode,
			} as UpdateBaseSubscriptionApi;
		} else {
			originalSubscription.status = "ACTIVE";
		}

		const activeSubscription = watchSubscriptions.find(
			(x) =>
				x.family_id === config.family_id && isSubscriptionActive(x.status) && !isArchivePlan(config, x.plan_id)
		);
		if (activeSubscription) {
			const activePlanId = activeSubscription.plan_id;
			const newPlan = getPlanBaseOtherPlan(config.family_id, activePlanId, originalSubscription.plan_id);
			if (newPlan) {
				originalSubscription.plan_id = newPlan.plan_id;
			}
		}
		return originalSubscription;
	};

	const findSimilarPlan = (
		plans: PriceConfiguratorPlanApi[],
		plan: PriceConfiguratorPlanApi,
		activePlanId?: string
	): PriceConfiguratorPlanApi | undefined => {
		const planSplit = (activePlanId ?? plan.plan_id).split("-");
		const plansWithSimilarity: { plan: PriceConfiguratorPlanApi; similarity: number }[] = [];

		plans.forEach((p) => {
			const pSplit = p.plan_id.split("-");
			let similarity = 0;

			planSplit.forEach((x, index) => {
				if (pSplit[index] && pSplit[index] === x) {
					similarity++;
				} else if (p.plan_id.endsWith(x)) {
					similarity += 0.5;
				}
			});
			plansWithSimilarity.push({
				plan: p,
				similarity,
			});
		});

		return plansWithSimilarity.sort((a, b) => b.similarity - a.similarity)[0]?.plan ?? plan;
	};

	const getPlanBaseOtherPlan = (
		familyId: string,
		activePlanId: string,
		planId: string,
		basedOnActivePlan?: boolean
	) => {
		const configFamily = props.config.contents.find((x) => x.family_id === familyId);
		if (!configFamily) return undefined;

		const licenseLevel = getLicenseLevel(configFamily, activePlanId);
		if (!licenseLevel) return undefined;

		const plan = configFamily.plans.find((x) => x.plan_id === planId);
		if (!plan) return undefined;

		const newPlan = findSimilarPlan(
			configFamily.plans.filter(
				(x) => plan && x.license_level === licenseLevel && x.edit_mode === plan.edit_mode
			),
			plan,
			basedOnActivePlan ? activePlanId : undefined
		);

		if (!newPlan) return undefined;
		return newPlan;
	};

	if (loading) {
		return <LoadingContainer />;
	}

	if (props.showSummaryPreview) {
		return (
			<SubscriptionSummaryPreview
				form={form}
				meDetails={props.meDetails}
				invoices={props.invoices}
				configs={configs}
				customer={subscriptions.customer}
				estimate={estimateSubscriptions}
				original={subscriptions}
				termsNamesToAccept={termsNamesToAccept}
			/>
		);
	}
	const fieldsRO = watchSubscriptions.filter((x) => x.edit_mode === "READ_ONLY" && x.status === "ACTIVE");
	const familys = props.config.contents.filter((x) => x.plans.some((x) => x.edit_mode === "EDITABLE"));
	const uninstalledContents = familys.filter(
		(fam) => !watchSubscriptions.some((f) => f.edit_mode === "EDITABLE" && f.family_id === fam.family_id)
	);

	return (
		<div className="row">
			<div className="col-xl-8">
				{subscriptionsVisibility === SubscriptionVisibility.VISIBLE && (
					<FormDirty isDirty={formState.isDirty}>
						<div className={"subscriptions-form-content"}>
							{estimating && <LoadingContainer />}
							{fieldsRO.map((field) => {
								const estimateSubscription = estimateSubscriptions.subscriptions.find(
									(x) => x.external_id === field.external_id
								);
								const originalSubscription = subscriptions.subscriptions.find(
									(x) => x.external_id === field.external_id
								);
								if (!originalSubscription) return "NO EXIST";
								const config = configs.contents.find((f) => f.family_id === field.family_id);
								const index = watchSubscriptions.indexOf(field);
								return (
									<ReadonlySubscription
										key={`${field.plan_id}_${field.external_id}_${field.family_id}`}
										handleChangePlan={handleChangePlan}
										original={originalSubscription}
										estimate={estimateSubscription}
										form={form}
										config={config}
										updateSubscription={field}
										index={index}
									/>
								);
							})}
							{familys.map((family) => {
								const editable = "EDITABLE";
								const updateSubscription = watchSubscriptions.find(
									(x) => x.family_id === family.family_id && x.edit_mode === editable
								);
								if (!updateSubscription) {
									return null;
								}
								const index = watchSubscriptions.indexOf(updateSubscription);
								const estimateSubscription = findSameSubscription(
									estimateSubscriptions.subscriptions,
									family.family_id,
									editable,
									undefined
								);
								const originalSubscription = findSameSubscription(
									subscriptions.subscriptions,
									family.family_id,
									editable,
									undefined
								);
								return (
									<SubscriptionConfigForm
										handleChangePlan={handleChangePlan}
										createDefaultSubscription={createDefaultSubscription}
										key={`${family.family_id}`}
										updateSubscription={updateSubscription}
										original={originalSubscription}
										estimate={estimateSubscription}
										config={family}
										index={index}
										form={form}
										handleRemoveSubscription={handleRemoveSubscription}
										handleUpdateSubscription={handleUpdateSubscription}
										rawSubscriptions={subscriptions}
										setTermsNamesToAccept={setTermsNamesToAccept}
										termsNamesToAccept={termsNamesToAccept}
										estimating={estimating}
									/>
								);
							})}
							{uninstalledContents.map((config) => {
								if (config.plans.some((x) => x.edit_mode === "EDITABLE"))
									return (
										<InactiveSubscription
											key={`inactive-${config.family_id}`}
											config={config}
											onAdd={onAddSubscription}
										/>
									);
								return <></>;
							})}
						</div>
					</FormDirty>
				)}
			</div>
			{isMobile ? (
				<StickySubscriptionsSummary
					update={() => estimate()}
					form={form}
					estimating={estimating}
					errors={errors}
					estimate={estimateSubscriptions}
					setShowSummaryPreview={setShowSummaryPreview}
					subscriptionsVisibility={subscriptionsVisibility}
					showSummaryPreview={props.showSummaryPreview}
				/>
			) : (
				<div className="col-xl-4">
					<SubscriptionsPaymentSummaryCard
						update={() => estimate()}
						form={form}
						estimating={estimating}
						errors={errors}
						estimate={estimateSubscriptions}
						setShowSummaryPreview={setShowSummaryPreview}
						subscriptionsVisibility={subscriptionsVisibility}
					/>
				</div>
			)}
		</div>
	);
};
export default SubscriptionsFormNew;
